import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams } from 'react-router-dom'
import {
  FileSpreadsheet,
  Download,
  Eye,
  Trash2,
  RefreshCw,
  Play,
  Zap,
  Table,
  AlertCircle,
  CheckCircle,
  Clock,
  X,
  ChevronDown,
  ChevronUp,
  Cpu,
} from 'lucide-react'
import * as api from '../services/api'
import type { GeneratedOutput, OutputType, OutputTypeInfo, OutputJobStatusResponse } from '../types'

// Fun loading messages for output generation
const loadingMessages = [
  'Spinning up the AGILE artifact factory...',
  'Teaching AI to write spreadsheets...',
  'Generating user stories from chaos...',
  'Converting requirements into rows...',
  'Summoning the CSV wizards...',
  'Crunching project data into perfection...',
  'Building your burndown from scratch...',
  'Assembling RACI matrix with care...',
  'Cross-referencing all the things...',
  'Making Smartsheets jealous...',
  'Turning documents into deliverables...',
  'Calculating story points with extreme precision...',
  'Mapping risks like a pro...',
  'Generating enough columns to impress your PM...',
]

const outputTypeIcons: Record<string, { icon: typeof FileSpreadsheet; color: string }> = {
  stories: { icon: FileSpreadsheet, color: 'blue' },
  taskboard: { icon: Table, color: 'purple' },
  raci: { icon: FileSpreadsheet, color: 'cyan' },
  risk_register: { icon: FileSpreadsheet, color: 'amber' },
  burndown: { icon: FileSpreadsheet, color: 'green' },
  status_report: { icon: FileSpreadsheet, color: 'pink' },
  meeting_log: { icon: FileSpreadsheet, color: 'indigo' },
  deliverables_tracker: { icon: FileSpreadsheet, color: 'emerald' },
  hours_tracking: { icon: FileSpreadsheet, color: 'orange' },
  capacity: { icon: FileSpreadsheet, color: 'violet' },
  story_points: { icon: FileSpreadsheet, color: 'rose' },
}

function getStatusBadge(status: string) {
  switch (status) {
    case 'completed':
      return (
        <span className="badge-green">
          <CheckCircle className="w-3 h-3" />
          Completed
        </span>
      )
    case 'generating':
      return (
        <span className="badge-blue">
          <RefreshCw className="w-3 h-3 animate-spin" />
          Generating
        </span>
      )
    case 'pending':
      return (
        <span className="badge-yellow">
          <Clock className="w-3 h-3" />
          Pending
        </span>
      )
    case 'error':
      return (
        <span className="badge-red">
          <AlertCircle className="w-3 h-3" />
          Error
        </span>
      )
    default:
      return <span className="badge-gray">{status}</span>
  }
}

interface Sparkle {
  id: number
  x: number
  y: number
  size: number
  opacity: number
  color: string
}

interface LoadingAnimationProps {
  message: string
  jobStatus?: OutputJobStatusResponse | null
}

function LoadingAnimation({ message, jobStatus }: LoadingAnimationProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [sparkles, setSparkles] = useState<Sparkle[]>([])
  const sparkleIdRef = useRef(0)

  // Mouse trail effect
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const colors = ['#a78bfa', '#22d3ee', '#fbbf24', '#f472b6', '#34d399']

    const handleMouseMove = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top

      // Only create sparkles if mouse is inside container
      if (x < 0 || x > rect.width || y < 0 || y > rect.height) return

      // Create new sparkle
      const newSparkle: Sparkle = {
        id: sparkleIdRef.current++,
        x,
        y,
        size: Math.random() * 12 + 6,
        opacity: 1,
        color: colors[Math.floor(Math.random() * colors.length)],
      }

      setSparkles((prev) => [...prev.slice(-20), newSparkle]) // Keep max 20 sparkles
    }

    container.addEventListener('mousemove', handleMouseMove)
    return () => container.removeEventListener('mousemove', handleMouseMove)
  }, [])

  // Fade out sparkles
  useEffect(() => {
    if (sparkles.length === 0) return

    const interval = setInterval(() => {
      setSparkles((prev) =>
        prev
          .map((s) => ({ ...s, opacity: s.opacity - 0.05, size: s.size * 0.95 }))
          .filter((s) => s.opacity > 0)
      )
    }, 50)

    return () => clearInterval(interval)
  }, [sparkles.length])

  const progressPercent = jobStatus?.progress_percent ?? 0
  const currentStep = jobStatus?.current_step ?? 0
  const totalSteps = jobStatus?.total_steps ?? 11

  return (
    <div
      ref={containerRef}
      className="card bg-gradient-to-br from-emerald-500/10 to-cyan-500/10 border-emerald-500/30 relative overflow-hidden cursor-crosshair"
    >
      {/* Sparkle trail */}
      {sparkles.map((sparkle) => (
        <div
          key={sparkle.id}
          className="absolute pointer-events-none"
          style={{
            left: sparkle.x,
            top: sparkle.y,
            transform: 'translate(-50%, -50%)',
            opacity: sparkle.opacity,
            transition: 'opacity 0.1s, transform 0.1s',
          }}
        >
          <svg
            width={sparkle.size}
            height={sparkle.size}
            viewBox="0 0 24 24"
            fill={sparkle.color}
            className="animate-spin"
            style={{ animationDuration: '1s' }}
          >
            <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
          </svg>
        </div>
      ))}

      <div className="flex flex-col items-center justify-center py-12 relative z-10">
        {/* Animated CPU/Brain icon */}
        <div className="relative mb-6">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-500/30 to-cyan-500/30 flex items-center justify-center animate-pulse">
            <Cpu className="w-10 h-10 text-emerald-400" />
          </div>
          {/* Orbiting dots */}
          <div className="absolute inset-0 animate-spin" style={{ animationDuration: '3s' }}>
            <div className="absolute -top-1 left-1/2 w-2 h-2 bg-cyan-400 rounded-full shadow-lg shadow-cyan-400/50" />
          </div>
          <div
            className="absolute inset-0 animate-spin"
            style={{ animationDuration: '2s', animationDirection: 'reverse' }}
          >
            <div className="absolute -bottom-1 left-1/2 w-2 h-2 bg-emerald-400 rounded-full shadow-lg shadow-emerald-400/50" />
          </div>
          <div className="absolute inset-0 animate-spin" style={{ animationDuration: '4s' }}>
            <div className="absolute top-1/2 -right-1 w-2 h-2 bg-amber-400 rounded-full shadow-lg shadow-amber-400/50" />
          </div>
        </div>

        {/* Real progress bar */}
        <div className="w-64 h-2 bg-slate-700/50 rounded-full overflow-hidden mb-2">
          <div
            className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        {/* Progress text */}
        <p className="text-slate-400 text-sm mb-4">
          Step {currentStep} of {totalSteps} ({progressPercent}%)
        </p>

        {/* Step list */}
        {jobStatus?.steps && (
          <div className="w-full max-w-md mb-6 space-y-2 max-h-80 overflow-y-auto">
            {jobStatus.steps.map((step, idx) => (
              <div
                key={idx}
                className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-all ${
                  step.status === 'running'
                    ? 'bg-emerald-500/20 border border-emerald-500/30'
                    : step.status === 'completed'
                      ? 'bg-green-500/10'
                      : step.status === 'failed'
                        ? 'bg-red-500/10'
                        : 'bg-slate-800/30'
                }`}
              >
                {step.status === 'completed' ? (
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                ) : step.status === 'running' ? (
                  <div className="w-4 h-4 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                ) : step.status === 'failed' ? (
                  <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                ) : (
                  <div className="w-4 h-4 border-2 border-slate-600 rounded-full flex-shrink-0" />
                )}
                <span
                  className={`text-sm ${
                    step.status === 'running'
                      ? 'text-emerald-300 font-medium'
                      : step.status === 'completed'
                        ? 'text-green-400'
                        : step.status === 'failed'
                          ? 'text-red-400'
                          : 'text-slate-500'
                  }`}
                >
                  {step.name}
                </span>
                {step.error && <span className="text-xs text-red-400 ml-auto">{step.error}</span>}
              </div>
            ))}
          </div>
        )}

        {/* Fun message */}
        <p className="text-slate-300 text-center font-medium">{message}</p>
        <p className="text-slate-500 text-sm mt-2">Move your mouse around for sparkles</p>
      </div>
    </div>
  )
}

function OutputPreviewModal({ output, onClose }: { output: GeneratedOutput; onClose: () => void }) {
  const [content, setContent] = useState<{
    preview_rows: string[][]
    row_count: number
  } | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await api.getOutputContent(output.id)
        setContent({
          preview_rows: response.data.preview_rows,
          row_count: response.data.row_count,
        })
      } catch (err) {
        console.error('Failed to fetch content:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchContent()
  }, [output.id])

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal max-w-4xl animate-scaleIn" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3 className="text-lg font-semibold text-slate-100">{output.filename}</h3>
            <p className="text-sm text-slate-400">
              {content?.row_count} rows • Generated{' '}
              {new Date(output.created_at).toLocaleDateString()}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => api.downloadOutput(output.id, output.filename)}
              className="btn-primary btn-sm"
            >
              <Download className="w-4 h-4" />
              Download
            </button>
            <button onClick={onClose} className="btn-ghost btn-icon btn-sm">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="modal-body">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="spinner" />
            </div>
          ) : content ? (
            <div className="table-container">
              <table className="table text-xs">
                <thead>
                  <tr>
                    {content.preview_rows[0]?.map((header, i) => (
                      <th key={i} className="whitespace-nowrap">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {content.preview_rows.slice(1).map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <td key={cellIndex} className="whitespace-nowrap max-w-xs truncate">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-slate-400 text-center py-8">Failed to load preview</p>
          )}
        </div>
      </div>
    </div>
  )
}

function OutputCard({
  output,
  onDelete,
  onPreview,
}: {
  output: GeneratedOutput
  onDelete: (id: string) => void
  onPreview: (output: GeneratedOutput) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const config = outputTypeIcons[output.output_type] || {
    icon: FileSpreadsheet,
    color: 'gray',
  }
  const Icon = config.icon

  return (
    <div className="card">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div
            className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${config.color}-500/20`}
          >
            <Icon className={`w-6 h-6 text-${config.color}-400`} />
          </div>
          <div>
            <h3 className="font-semibold text-slate-100">{output.filename}</h3>
            <div className="flex items-center gap-2 mt-1">
              {getStatusBadge(output.status)}
              <span className="text-xs text-slate-500">
                {new Date(output.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {output.status === 'completed' && (
            <>
              <button
                onClick={() => onPreview(output)}
                className="btn-ghost btn-icon btn-sm"
                title="Preview"
              >
                <Eye className="w-4 h-4" />
              </button>
              <button
                onClick={() => api.downloadOutput(output.id, output.filename)}
                className="btn-ghost btn-icon btn-sm"
                title="Download"
              >
                <Download className="w-4 h-4" />
              </button>
            </>
          )}
          <button
            onClick={() => onDelete(output.id)}
            className="btn-ghost btn-icon btn-sm text-red-400 hover:text-red-300"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <button onClick={() => setExpanded(!expanded)} className="btn-ghost btn-icon btn-sm">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {expanded && output.source_documents && output.source_documents.length > 0 && (
        <div className="mt-4 p-3 bg-slate-800/30 rounded-lg">
          <h4 className="text-sm font-medium text-slate-400 mb-2">Source Documents</h4>
          <ul className="text-xs text-slate-500 list-disc list-inside">
            {output.source_documents.map((doc, idx) => (
              <li key={idx}>{doc}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export default function Outputs() {
  const { projectId } = useParams<{ projectId: string }>()
  const [outputs, setOutputs] = useState<GeneratedOutput[]>([])
  const [outputTypes, setOutputTypes] = useState<OutputTypeInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [previewOutput, setPreviewOutput] = useState<GeneratedOutput | null>(null)
  const [selectedTypes, setSelectedTypes] = useState<OutputType[]>([])
  const [jobStatus, setJobStatus] = useState<OutputJobStatusResponse | null>(null)
  const [loadingMessage, setLoadingMessage] = useState(loadingMessages[0])
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Rotate loading messages
  useEffect(() => {
    if (generating !== 'all') return
    const interval = setInterval(() => {
      setLoadingMessage(loadingMessages[Math.floor(Math.random() * loadingMessages.length)])
    }, 3000)
    return () => clearInterval(interval)
  }, [generating])

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
      }
    }
  }, [])

  const fetchData = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    try {
      const [outputsRes, typesRes] = await Promise.all([
        api.getOutputs(projectId),
        api.getOutputTypes(projectId),
      ])
      setOutputs(outputsRes.data.outputs || [])
      setOutputTypes(typesRes.data)
    } catch (err) {
      console.error('Failed to fetch outputs:', err)
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const pollJobStatus = useCallback(
    async (jobId: string) => {
      if (!projectId) return

      try {
        const response = await api.getOutputJobStatus(projectId, jobId)
        setJobStatus(response.data)

        if (response.data.status === 'completed' || response.data.status === 'failed') {
          // Stop polling
          if (pollingRef.current) {
            clearInterval(pollingRef.current)
            pollingRef.current = null
          }

          if (response.data.status === 'completed') {
            await fetchData()
          } else if (response.data.error_message) {
            setError(response.data.error_message)
          }

          setGenerating(null)
          setJobStatus(null)
        }
      } catch (err) {
        console.error('Failed to poll job status:', err)
        // Don't stop polling on transient errors
      }
    },
    [projectId, fetchData]
  )

  const handleGenerate = async (outputType: OutputType) => {
    if (!projectId) return
    setGenerating(outputType)
    setError(null)
    try {
      await api.generateOutput(projectId, outputType)
      await fetchData()
    } catch (err) {
      console.error('Failed to generate output:', err)
      setError('Failed to generate output. Please try again.')
    } finally {
      setGenerating(null)
    }
  }

  const handleGenerateAll = async () => {
    if (!projectId) return
    setGenerating('all')
    setError(null)
    setJobStatus(null)
    setLoadingMessage(loadingMessages[Math.floor(Math.random() * loadingMessages.length)])

    try {
      // Start the output generation job
      const response = await api.generateAllOutputs(projectId)
      const jobId = response.data.job_id

      // Start polling for status
      pollingRef.current = setInterval(() => {
        pollJobStatus(jobId)
      }, 2000) // Poll every 2 seconds

      // Also poll immediately
      pollJobStatus(jobId)
    } catch (err: unknown) {
      console.error('Failed to start output generation:', err)
      const errorMessage =
        err instanceof Error && err.message.includes('429')
          ? 'Azure OpenAI is rate-limited. Please wait a few minutes and try again.'
          : 'Output generation failed to start. Please try again in a moment.'
      setError(errorMessage)
      setGenerating(null)
    }
  }

  const handleDelete = async (outputId: string) => {
    if (!confirm('Yeet this output into the digital void?')) return
    try {
      await api.deleteOutput(outputId)
      await fetchData()
    } catch (err) {
      console.error('Failed to delete output:', err)
      setError('Failed to delete output')
    }
  }

  const toggleTypeSelection = (type: OutputType) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    )
  }

  const isGeneratingAll = generating === 'all'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Generated Outputs</h1>
          <p className="text-slate-400 mt-1">
            Generate CSV files for Smartsheets and other project artifacts
          </p>
        </div>
        {!isGeneratingAll && (
          <button
            onClick={handleGenerateAll}
            disabled={generating !== null}
            className="btn-primary"
          >
            {generating !== null ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Zap className="w-4 h-4" />
                Generate All Outputs
              </>
            )}
          </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="alert-error">
          <AlertCircle className="w-5 h-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Loading Animation */}
      {isGeneratingAll && <LoadingAnimation message={loadingMessage} jobStatus={jobStatus} />}

      {/* Output Type Grid - hide during generation */}
      {!isGeneratingAll && (
        <div className="card">
          <h2 className="text-lg font-semibold text-slate-100 mb-4">Available Output Types</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {outputTypes.map((type) => {
              const config = outputTypeIcons[type.type] || {
                icon: FileSpreadsheet,
                color: 'gray',
              }
              const Icon = config.icon
              const isSelected = selectedTypes.includes(type.type)
              const existingOutput = outputs.find((o) => o.output_type === type.type)

              return (
                <div
                  key={type.type}
                  className={`p-4 rounded-lg border transition-all cursor-pointer ${
                    isSelected
                      ? 'border-blue-500/50 bg-blue-500/10'
                      : 'border-slate-700/50 bg-slate-800/30 hover:border-slate-600/50'
                  }`}
                  onClick={() => toggleTypeSelection(type.type)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center bg-${config.color}-500/20`}
                    >
                      <Icon className={`w-4 h-4 text-${config.color}-400`} />
                    </div>
                    <div className="flex items-center gap-1">
                      {existingOutput && <span className="badge-green text-xs">Generated</span>}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleGenerate(type.type)
                        }}
                        disabled={generating !== null}
                        className="btn-ghost btn-icon btn-sm"
                        title="Generate"
                      >
                        {generating === type.type ? (
                          <RefreshCw className="w-3 h-3 animate-spin" />
                        ) : (
                          <Play className="w-3 h-3" />
                        )}
                      </button>
                    </div>
                  </div>
                  <h3 className="font-medium text-slate-200 text-sm">{type.name}</h3>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{type.description}</p>
                </div>
              )
            })}
          </div>
          {selectedTypes.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-700/50 flex items-center justify-between">
              <p className="text-sm text-slate-400">
                {selectedTypes.length} output type{selectedTypes.length !== 1 ? 's' : ''} selected
              </p>
              <button onClick={() => setSelectedTypes([])} className="btn-ghost btn-sm">
                Clear Selection
              </button>
            </div>
          )}
        </div>
      )}

      {/* Generated Outputs List - hide during generation */}
      {!isGeneratingAll && (
        <>
          {loading ? (
            <div className="card">
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="skeleton w-12 h-12 rounded-xl" />
                    <div className="flex-1">
                      <div className="skeleton h-5 w-48 mb-2" />
                      <div className="skeleton h-4 w-32" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : outputs.length > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-200">Generated Files</h2>
                <span className="text-sm text-slate-400">{outputs.length} files</span>
              </div>
              {outputs.map((output) => (
                <OutputCard
                  key={output.id}
                  output={output}
                  onDelete={handleDelete}
                  onPreview={setPreviewOutput}
                />
              ))}
            </div>
          ) : (
            <div className="card text-center py-12">
              <FileSpreadsheet className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-300 mb-2">No outputs generated yet</h3>
              <p className="text-slate-500 mb-4">
                Generate CSV files for user stories, taskboards, RACI matrices, and more
              </p>
              <button onClick={handleGenerateAll} className="btn-primary">
                <Zap className="w-4 h-4" />
                Generate All Outputs
              </button>
            </div>
          )}
        </>
      )}

      {/* Preview Modal */}
      {previewOutput && (
        <OutputPreviewModal output={previewOutput} onClose={() => setPreviewOutput(null)} />
      )}
    </div>
  )
}
